'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cake, CheckCircle, Gift, Info, Sparkle } from '@phosphor-icons/react';
import { ThemedImage } from '@/components/ui/themed-image';
import { PromotionModal } from './promotion-modal';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';

interface BirthdayModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Условия акции с сервера (site_promotion_terms). Пусто или не передали —
   * показываем зашитый текст: пустой блок условий у публичной оферты хуже,
   * чем устаревший.
   */
  terms?: readonly string[];
}

/** Условия на 16.08.2026 — запасной текст, если сервер не ответил. */
const FALLBACK_TERMS = [
  'Акция действует 3 дня до и 3 дня после дня рождения',
  'Минимальная сумма заказа: 1500₽',
  'Необходимо ввести промокод ДР в корзине',
  'Подарочный ролл добавляется автоматически',
  'Акция не суммируется с другими промокодами',
  'Действует только при онлайн-заказе',
  'В сумму чека включена стоимость только выбранных вами блюд. Обратите внимание: оплата доставки, приборов и соусов рассчитывается отдельно и не включается в общую сумму чека.',
];

// ID ролла для дня рождения (из мобильного приложения)
const BIRTHDAY_ROLL_ID = 81;

export function BirthdayModal({ open, onClose, terms }: BirthdayModalProps) {
  const [kapuchinoRoll, setKapuchinoRoll] = useState<ProductWithCategories | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!open) return;

    const fetchKapuchino = async () => {
      try {
        setIsLoading(true);
        // Загружаем ролл для дня рождения
        try {
          const product = await getProductById(BIRTHDAY_ROLL_ID, true);
          setKapuchinoRoll(product);
        } catch {
          // Если не нашли, используем placeholder
          setKapuchinoRoll(null);
        }
      } catch (err) {
        console.error('Ошибка при загрузке ролла:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKapuchino();
  }, [open]);

  return (
    <PromotionModal
      open={open}
      onClose={onClose}
      title="Подарок на День рождения"
      icon={<Cake className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />}
    >
      <div className="px-4 pt-5 pb-4 md:px-6 md:pt-6 md:pb-6 space-y-6 md:space-y-8">
        {/* Description */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-br from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-card bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
              <Sparkle className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div className="flex-1">
              <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-2">
                🎂 С Днём рождения!
              </h3>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed">
                Нашим именинникам — ролл в подарок! Оформляйте заказ от 1500₽ за 3 дня до или 3 дня после
                дня рождения и получайте бесплатный ролл!
              </p>
            </div>
          </div>
        </div>

        {/* Birthday Roll Info */}
        {kapuchinoRoll && (
          <div className="rounded-card overflow-hidden bg-[var(--surface-primary)] border border-[var(--border-default)]">
            <div className="p-4 md:p-6 border-b border-[var(--border-default)] bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)]">
              <p className="text-xs font-bold text-[var(--primary)] uppercase tracking-wider mb-1">
                Ваш подарок
              </p>
              <h3 className="text-xl md:text-2xl font-bold font-heading text-[var(--text-primary)]">
                {kapuchinoRoll.name}
              </h3>
            </div>
            
            {/* Image Container */}
            <div className="p-4 md:p-6">
              <div className="relative w-full h-48 md:h-64 rounded-card overflow-hidden border-2 border-[var(--primary)]/20 bg-[var(--surface-tertiary)] mb-4">
                <ThemedImage
                  src={kapuchinoRoll.image_url}
                  alt={kapuchinoRoll.name}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
              
              {kapuchinoRoll.description && (
                <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed whitespace-pre-line mb-4">
                  {kapuchinoRoll.description}
                </p>
              )}
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                {kapuchinoRoll.weight && (
                  <div className="px-4 py-3 rounded-card bg-[var(--primary-subtle)]">
                    <p className="text-xs font-semibold text-[var(--primary)] uppercase tracking-wide mb-1">Вес</p>
                    <p className="text-lg font-bold text-[var(--text-primary)]">{kapuchinoRoll.weight}</p>
                  </div>
                )}
                {/* Плашка «Порций» удалена как недостижимая, а не как решение
                    её не показывать. Она читала kapuchinoRoll.pieces, но поля
                    pieces нет ни в ответе сервера, ни в схеме товаров: во всём
                    бэкенде это слово встречается только как ПЛАН на будущую
                    колонку (migrations/product_weight_numeric.sql). Реальное
                    поле называется serving_size — так его и читает соседний
                    status-rolls-promo. Условие всегда было ложным, поэтому на
                    экране ничего не менялось и не меняется.
                    Показывать ли здесь число порций — вопрос к владельцу
                    (это содержимое акции, а не оформление), поэтому переключать
                    на serving_size в чисто визуальной волне не стал. */}
              </div>
              
              {kapuchinoRoll.ingredients && (
                <div>
                  <p className="text-xs font-bold text-[var(--primary)] uppercase tracking-wide mb-3">Состав</p>
                  <div className="flex flex-wrap gap-2">
                    {kapuchinoRoll.ingredients.split(',').map((ingredient: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 rounded-pill bg-[var(--primary-subtle)] border border-[var(--primary)]/20 text-sm font-semibold text-[var(--text-primary)]"
                      >
                        {ingredient.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Promocode Info */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)] border-2 border-dashed border-[var(--primary)]">
          <p className="text-xs font-bold text-[var(--primary)] uppercase tracking-wide mb-2">Промокод</p>
          <div className="flex items-center justify-between gap-4">
            <p className="text-3xl md:text-4xl font-black text-[var(--primary)] tracking-wider">ДР</p>
            <button
              onClick={() => {
                navigator.clipboard.writeText('ДР');
              }}
              className="px-4 py-2 rounded-card bg-[var(--primary)] text-white font-bold text-sm hover:opacity-90 transition-opacity"
            >
              Копировать
            </button>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mt-3">
            Введите промокод ДР в корзине при заказе от 1500₽
          </p>
        </div>

        {/* How it works */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-[var(--primary)]" weight="fill" />
            <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)]">
              Как получить подарок?
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">1</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Убедитесь, что в вашем профиле указана дата рождения
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">2</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Оформите заказ от 1500₽ в период действия акции (за 3 дня до или 3 дня после дня рождения)
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">3</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Получите бесплатный ролл в подарок!
              </p>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div>
          <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-3">
            Условия акции
          </h3>
          <div className="space-y-2">
            {(terms && terms.length > 0 ? terms : FALLBACK_TERMS).map((condition, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-pill bg-[var(--primary-subtle)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[var(--primary)]" weight="fill" />
                </div>
                <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                  {condition}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlight */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-card bg-[var(--primary)] flex items-center justify-center">
              <Cake className="w-6 h-6 text-white" weight="fill" />
            </div>
            <div>
              <p className="text-base md:text-lg text-[var(--text-primary)] font-bold mb-1">
                🎂 С Днём рождения!
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                Получите бесплатный ролл в ваш особенный день!
              </p>
            </div>
          </div>
        </div>
      </div>
    </PromotionModal>
  );
}

